/**
 * Horizontal Facets Web Component
 * Custom filter UI for collection/search pages
 */

if (!customElements.get('ds-horizontal-facets')) {
  customElements.define(
    'ds-horizontal-facets',
    class HorizontalFacets extends HTMLElement {
      abortController = undefined;
      pubsubUnsubscribe = undefined;
      lockTimer = null;
      activeTooltip = null;
      tooltipOrigin = null;
      tipOffset = { x: 10, y: 10 };
      elements = {};

      constructor() {
        super();
      }

      connectedCallback() {
        this.abortController = new AbortController();
        this.cacheElements();
        this.init();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.pubsubUnsubscribe?.();
        if (this.lockTimer) clearTimeout(this.lockTimer);
        if (this.activeTooltip) {
          this.restoreTooltip(this.activeTooltip);
        }
      }

      cacheElements() {
        this.elements = {
          form: this.querySelector('form[is="facet-form"]'),
          filters: this.querySelector('.ds-hf__filters'),
          moreFiltersBtn: this.querySelector('[data-more-filters-toggle]'),
          chips: this.querySelectorAll('.ds-hf__chip')
        };
      }

      init() {
        this.lockAllSummaryHeights(); // Lock heights immediately on init
        this.overrideAccordionAnimation(); // Override accordion animation to use fixed height
        this.initPopupPositioning();
        this.initChipWidthLocking();
        this.initTooltips();
        this.initMoreFiltersButton();
        this.restoreFiltersExpandedState();
        this.initializeAllFilters();
        this.syncAllActiveFiltersToForm();
        this.subscribeToPubSub();
      }

      // Lock all summary heights to prevent any animation artifacts
      lockAllSummaryHeights() {
        const summaries = this.querySelectorAll('.ds-hf__chip-summary');
        summaries.forEach((summary) => {
          summary.style.setProperty('height', '39px', 'important');
          summary.style.setProperty('min-height', '39px', 'important');
          summary.style.setProperty('max-height', '39px', 'important');
        });
      }

      // Override accordion animation to use fixed summary height
      overrideAccordionAnimation() {
        const chips = this.querySelectorAll('.ds-hf__chip[is="accordion-details"]');
        chips.forEach((chip) => {
          // Store the original animateAccordion method
          const originalAnimate = chip.animateAccordion;

          if (originalAnimate) {
            // Override with a version that uses fixed 39px summary height
            chip.animateAccordion = async function(isOpen) {
              this.style.overflow = 'hidden';
              const summaryElement = this.querySelector('.ds-hf__chip-summary');
              const contentElement = this.querySelector('summary + *');

              // Force summary to exact 39px before animation
              if (summaryElement) {
                summaryElement.style.setProperty('height', '39px', 'important');
                summaryElement.style.setProperty('min-height', '39px', 'important');
                summaryElement.style.setProperty('max-height', '39px', 'important');
              }

              if (isOpen) {
                this.setAttribute('open', '');

                await FoxTheme.Motion.timeline([
                  [
                    this,
                    { height: ['39px', `${this.scrollHeight}px`] }, // Use fixed 39px instead of clientHeight
                    { duration: 0.25, easing: 'ease' },
                  ],
                  [
                    contentElement,
                    { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] },
                    { duration: 0.15, at: '<' },
                  ],
                ]).finished;
              } else {
                await FoxTheme.Motion.timeline([
                  [
                    contentElement,
                    { opacity: [1, 0], transform: ['translateY(0)', 'translateY(10px)'] },
                    { duration: 0.15 },
                  ],
                  [
                    this,
                    { height: [`${this.scrollHeight}px`, '39px'] }, // Use fixed 39px instead of clientHeight
                    { duration: 0.25, easing: 'ease', at: '<' },
                  ],
                ]).finished;

                this.removeAttribute('open');
              }

              this.style.overflow = '';
              this.style.height = '';

              // Ensure summary height remains locked after animation
              if (summaryElement) {
                summaryElement.style.setProperty('height', '39px', 'important');
              }

              this.handleAfterAnimated();
            };
          }
        });
      }

      // ==================== Popup Positioning ====================

      placePopover(details) {
        const summary = details.querySelector('.ds-hf__chip-summary');
        const panel = details.querySelector('.ds-hf__content');
        if (!summary || !panel) return;

        const rect = summary.getBoundingClientRect();
        const panelWidth = Math.max(panel.offsetWidth || 260, 260);

        // Use fixed so it escapes any overflow ancestors
        panel.classList.add('is-portal');
        panel.style.position = 'fixed';
        panel.style.zIndex = '1000';
        panel.style.left = Math.round(Math.min(rect.left, window.innerWidth - panelWidth - 8)) + 'px';
        panel.style.top = Math.round(rect.bottom + 4) + 'px';
        panel.style.minWidth = panelWidth + 'px';
        panel.style.maxHeight = '350px';

        // Add scroll indicator to scrollable elements
        const scrollableElements = panel.querySelectorAll('.ds-hf__size-filter, ul.swatches, ul.grid, .ds-hf__color-swatches, .accordion-details__content');
        scrollableElements.forEach((scrollable) => {
          if (!scrollable.querySelector('.ds-hf__scroll-indicator')) {
            const scrollIndicator = document.createElement('div');
            scrollIndicator.className = 'ds-hf__scroll-indicator';
            scrollable.appendChild(scrollIndicator);
          }
        });

        // Setup scroll indicator
        this.setupScrollIndicator(panel);
      }

      setupScrollIndicator(panel) {
        const scrollableElements = panel.querySelectorAll('.ds-hf__size-filter, ul.swatches, ul.grid, .ds-hf__color-swatches, .accordion-details__content');

        scrollableElements.forEach((scrollable) => {
          const scrollIndicator = scrollable.querySelector('.ds-hf__scroll-indicator');
          if (!scrollIndicator) return;

          const checkScrollPosition = () => {
            const hasScroll = scrollable.scrollHeight > scrollable.clientHeight;
            const scrollBottom = scrollable.scrollHeight - scrollable.clientHeight - scrollable.scrollTop;
            const isAtBottom = scrollBottom <= 5; // 5px tolerance

            if (!hasScroll || isAtBottom) {
              scrollIndicator.classList.add('is-hidden');
            } else {
              scrollIndicator.classList.remove('is-hidden');
            }
          };

          // Check initially
          setTimeout(checkScrollPosition, 50);

          // Listen to scroll events
          scrollable.addEventListener('scroll', checkScrollPosition, {
            signal: this.abortController.signal
          });

          // Also check on resize
          window.addEventListener('resize', checkScrollPosition, {
            signal: this.abortController.signal
          });
        });
      }

      resetPopover(details) {
        const panel = details.querySelector('.ds-hf__content');
        if (!panel) return;

        // Remove scroll indicators from all scrollable elements
        const scrollIndicators = panel.querySelectorAll('.ds-hf__scroll-indicator');
        scrollIndicators.forEach((indicator) => {
          indicator.remove();
        });

        panel.classList.remove('is-portal');
        panel.removeAttribute('style');
      }

      handleToggle(e) {
        const details = e.target;
        if (!details.matches || !details.matches('.ds-hf__chip')) return;

        // Lock summary height immediately and force reflow to prevent resize during animation
        const summary = details.querySelector('.ds-hf__chip-summary');
        if (summary) {
          summary.style.setProperty('height', '39px', 'important');
          summary.style.setProperty('min-height', '39px', 'important');
          summary.style.setProperty('max-height', '39px', 'important');
          // Force immediate reflow
          void summary.offsetHeight;
        }

        if (details.open) {
          // Close all other open chips immediately
          this.querySelectorAll('.ds-hf__chip[open]').forEach((chip) => {
            if (chip !== details) {
              this.resetPopover(chip);
              chip.open = false;
            }
          });
          this.placePopover(details);
        } else {
          this.resetPopover(details);
        }
      }

      handleSummaryClick(e) {
        const summary = e.target.closest('.ds-hf__chip-summary');
        if (!summary) return;

        const clickedChip = summary.closest('.ds-hf__chip');
        if (!clickedChip) return;

        // If this chip is closed and we're about to open it, close all others first
        if (!clickedChip.open) {
          this.querySelectorAll('.ds-hf__chip[open]').forEach((chip) => {
            if (chip !== clickedChip) {
              this.resetPopover(chip);
              chip.open = false;
            }
          });
        }
      }

      handleOutsideClick(event) {
        const openChips = this.querySelectorAll('.ds-hf__chip[open]');

        openChips.forEach((chip) => {
          const content = chip.querySelector('.ds-hf__content');
          const summary = chip.querySelector('.ds-hf__chip-summary');

          // Check if click is outside both content and summary
          if (content && !content.contains(event.target) &&
              summary && !summary.contains(event.target)) {
            this.resetPopover(chip);
            chip.open = false;
          }
        });
      }

      relayoutOpen() {
        document.querySelectorAll('.ds-hf__chip[open]').forEach((chip) => {
          this.placePopover(chip);
        });
      }

      initPopupPositioning() {
        // Delegate toggle events
        this.addEventListener('toggle', this.handleToggle.bind(this), {
          capture: true,
          signal: this.abortController.signal
        });

        // Close other chips when clicking on a summary
        this.addEventListener('click', this.handleSummaryClick.bind(this), {
          capture: true,
          signal: this.abortController.signal
        });

        // Close popups when clicking outside
        document.addEventListener('click', this.handleOutsideClick.bind(this), {
          capture: true,
          signal: this.abortController.signal
        });

        // Reposition on viewport changes
        window.addEventListener('resize', this.relayoutOpen.bind(this), {
          signal: this.abortController.signal
        });
        window.addEventListener('scroll', this.relayoutOpen.bind(this), {
          capture: true,
          signal: this.abortController.signal
        });

        // Close via explicit close button inside popup
        this.addEventListener('click', (e) => {
          const closeBtn = e.target.closest('.ds-hf__close');
          if (!closeBtn) return;
          const chip = closeBtn.closest('details.ds-hf__chip');
          if (chip) {
            this.resetPopover(chip);
            chip.open = false;
          }
        }, {
          capture: true,
          signal: this.abortController.signal
        });
      }

      // ==================== Chip Width Locking ====================

      lockChipWidths(container) {
        const chips = (container || this).querySelectorAll('.ds-hf__chip-summary');
        chips.forEach((el) => {
          const w = el.offsetWidth; // include padding/border
          el.style.width = w + 'px';
          el.style.flexBasis = w + 'px';
          // Lock height with !important to prevent any resizing
          el.style.setProperty('height', '39px', 'important');
          el.style.setProperty('min-height', '39px', 'important');
          el.style.setProperty('max-height', '39px', 'important');
        });
      }

      unlockChipWidths(container) {
        const chips = (container || this).querySelectorAll('.ds-hf__chip-summary');
        chips.forEach((el) => {
          el.style.removeProperty('width');
          el.style.removeProperty('flex-basis');
          el.style.removeProperty('height');
        });
      }

      initChipWidthLocking() {
        if (!this.elements.form) return;

        this.elements.form.addEventListener('change', () => {
          this.lockChipWidths(this.elements.form);
          if (this.lockTimer) clearTimeout(this.lockTimer);
          this.lockTimer = setTimeout(() => {
            this.unlockChipWidths(this.elements.form);
          }, 800); // matches typical ajax update duration
        }, { signal: this.abortController.signal });

        // Also observe the filters container; when it mutates, release lock shortly after
        if (window.MutationObserver && this.elements.filters) {
          const mo = new MutationObserver(() => {
            if (this.lockTimer) clearTimeout(this.lockTimer);
            this.lockTimer = setTimeout(() => {
              this.unlockChipWidths(this.elements.form);
            }, 200);
          });
          mo.observe(this.elements.filters, { childList: true, subtree: true });
        }
      }

      // ==================== Tooltips ====================

      attachToBody(el) {
        this.tooltipOrigin = { parent: el.parentNode, next: el.nextSibling };
        document.body.appendChild(el);
        el.style.display = 'block';
        el.style.position = 'fixed';
        el.style.zIndex = '1001';
      }

      restoreTooltip(el) {
        if (!this.tooltipOrigin || !this.tooltipOrigin.parent) return;
        el.style.display = 'none';
        el.style.left = '';
        el.style.top = '';
        el.style.position = '';
        el.style.zIndex = '';
        if (this.tooltipOrigin.next && this.tooltipOrigin.next.parentNode === this.tooltipOrigin.parent) {
          this.tooltipOrigin.parent.insertBefore(el, this.tooltipOrigin.next);
        } else {
          this.tooltipOrigin.parent.appendChild(el);
        }
        this.tooltipOrigin = null;
      }

      positionTooltipFixed(el, evt) {
        const x = Math.min(evt.clientX + this.tipOffset.x, window.innerWidth - el.offsetWidth - 8);
        const y = Math.min(evt.clientY + this.tipOffset.y, window.innerHeight - el.offsetHeight - 8);
        el.style.left = x + 'px';
        el.style.top = y + 'px';
      }

      handleTooltipMouseEnter(e) {
        const holder = e.target.closest('[data-tooltip]');
        if (!holder || !this.contains(holder)) return;
        const tip = holder.querySelector('.tooltip');
        if (!tip) return;
        this.activeTooltip = tip;
        this.attachToBody(this.activeTooltip);
        this.positionTooltipFixed(this.activeTooltip, e);
      }

      handleTooltipMouseMove(e) {
        if (!this.activeTooltip) return;
        this.positionTooltipFixed(this.activeTooltip, e);
      }

      hideAndRestoreTooltip() {
        if (!this.activeTooltip) return;
        this.restoreTooltip(this.activeTooltip);
        this.activeTooltip = null;
      }

      initTooltips() {
        this.addEventListener('mouseenter', this.handleTooltipMouseEnter.bind(this), {
          capture: true,
          signal: this.abortController.signal
        });

        this.addEventListener('mousemove', this.handleTooltipMouseMove.bind(this), {
          capture: true,
          signal: this.abortController.signal
        });

        this.addEventListener('mouseleave', this.hideAndRestoreTooltip.bind(this), {
          capture: true,
          signal: this.abortController.signal
        });

        this.addEventListener('click', this.hideAndRestoreTooltip.bind(this), {
          capture: true,
          signal: this.abortController.signal
        });
      }

      // ==================== More Filters Button ====================

      initMoreFiltersButton() {
        if (!this.elements.moreFiltersBtn) return;

        this.elements.moreFiltersBtn.addEventListener('click', () => {
          const hiddenChips = this.querySelectorAll('.ds-hf__chip--hidden');

          // Show all hidden filters
          hiddenChips.forEach((chip) => {
            chip.classList.add('is-visible');
          });

          // Hide the button after expanding
          this.elements.moreFiltersBtn.classList.add('is-expanded');

          // Add hidden parameter to form to persist expanded state
          if (this.elements.form) {
            // Remove existing parameter if present
            const existingInput = this.elements.form.querySelector('input[name="show_all_filters"]');
            if (existingInput) {
              existingInput.remove();
            }

            // Add new hidden input
            const hiddenInput = document.createElement('input');
            hiddenInput.type = 'hidden';
            hiddenInput.name = 'show_all_filters';
            hiddenInput.value = '1';
            this.elements.form.appendChild(hiddenInput);
          }

          // Update URL immediately with replaceState (no new history entry)
          const url = new URL(window.location);
          url.searchParams.set('show_all_filters', '1');
          window.history.replaceState({}, '', url);
        }, { signal: this.abortController.signal });
      }

      restoreFiltersExpandedState() {
        // Check if filters should be expanded (from form parameter or URL)
        const shouldExpand = this.isFiltersExpanded();

        if (shouldExpand) {
          const hiddenChips = this.querySelectorAll('.ds-hf__chip--hidden');

          // Show all hidden filters
          hiddenChips.forEach((chip) => {
            chip.classList.add('is-visible');
          });

          // Hide the button
          if (this.elements.moreFiltersBtn) {
            this.elements.moreFiltersBtn.classList.add('is-expanded');
          }

          // Add hidden input to form if URL parameter is present
          if (this.elements.form) {
            const existingInput = this.elements.form.querySelector('input[name="show_all_filters"]');
            if (!existingInput) {
              const hiddenInput = document.createElement('input');
              hiddenInput.type = 'hidden';
              hiddenInput.name = 'show_all_filters';
              hiddenInput.value = '1';
              this.elements.form.appendChild(hiddenInput);
            }
          }
        }
      }

      isFiltersExpanded() {
        // Check form parameter
        if (this.elements.form) {
          const formInput = this.elements.form.querySelector('input[name="show_all_filters"]');
          if (formInput && formInput.value === '1') {
            return true;
          }
        }

        // Check URL parameter
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('show_all_filters') === '1';
      }

      // ==================== Size Filter ====================

      initSizeFilter(sizeFilter) {
        // Check if already initialized to avoid duplicate event listeners
        if (sizeFilter.hasAttribute('data-filter-initialized')) {
          return;
        }
        sizeFilter.setAttribute('data-filter-initialized', 'true');

        // Get references
        const detailsElement = sizeFilter.closest('details.ds-hf__chip');
        const facetForm = this.elements.form;
        const filterParamName = sizeFilter.getAttribute('data-filter-param-name');

        // Check if this is the UK size filter (should be hidden and not directly interacted with)
        const isUkSizeFilter = detailsElement && detailsElement.hasAttribute('data-uk-size-filter');

        // Find the UK size filter element (for dual-filter routing)
        const ukSizeFilterChip = this.querySelector('.ds-hf__chip[data-uk-size-filter]');
        const ukSizeFilter = ukSizeFilterChip ? ukSizeFilterChip.querySelector('[data-size-filter]') : null;
        const ukFilterParamName = ukSizeFilter ? ukSizeFilter.getAttribute('data-filter-param-name') : null;

        // localStorage key (same as size-selector.js for consistency)
        const STORAGE_KEY = 'lugina_size_unit_preference';

        // Get size map from data attribute
        const sizeMapAttr = sizeFilter.getAttribute('data-size-map');
        let sizeMap = {};

        // Load saved unit preference from localStorage or default to EU
        // NOTE: This will be overridden by getActiveValuesFromUrl() if there are active filters
        let currentUnit = this.getSavedUnitPreference(STORAGE_KEY) || 'eu';

        try {
          if (sizeMapAttr) {
            sizeMap = JSON.parse(sizeMapAttr);
          }
        } catch (e) {
          console.error('Failed to parse size map:', e);
        }

        // IMPORTANT: Skip initialization for UK filter (it's hidden and only used internally)
        if (isUkSizeFilter) {
          return;
        }

        // Function to sort size items numerically (Shopify sorts alphabetically by default)
        // Only sort EU and UK sizes filters
        const sortSizeItemsNumerically = () => {
          // Only sort if this is the EU or UK size filter
          if (!filterParamName || (!filterParamName.includes('eu_sizes') && !filterParamName.includes('uk_sizes'))) return;

          const sizeGrid = sizeFilter.querySelector('.ds-hf__size-grid');
          if (!sizeGrid) return;

          const sizeItems = Array.from(sizeGrid.querySelectorAll('.ds-hf__size-item'));

          // Sort items by their numeric value
          sizeItems.sort((a, b) => {
            const aValue = parseFloat(a.querySelector('.ds-hf__size-input')?.value || '0');
            const bValue = parseFloat(b.querySelector('.ds-hf__size-input')?.value || '0');
            return aValue - bValue;
          });

          // Re-append items in sorted order
          sizeItems.forEach((item) => {
            sizeGrid.appendChild(item);
          });
        };

        // Sort size items on initialization
        sortSizeItemsNumerically();

        // Function to initialize labels (store EU sizes)
        const initializeLabels = () => {
          const sizeLabels = sizeFilter.querySelectorAll('.ds-hf__size-label');
          sizeLabels.forEach((label) => {
            if (!label.hasAttribute('data-eu-size')) {
              const euSize = label.textContent.trim();
              label.setAttribute('data-eu-size', euSize);
            }
          });
          return sizeLabels;
        };

        // Function to switch sizes between EU and UK
        const switchSizes = (unit) => {
          const sizeLabels = initializeLabels();
          currentUnit = unit;

          // Track which UK sizes have already been shown to avoid duplicates
          const shownUkSizes = new Set();

          sizeLabels.forEach((label) => {
            const euSize = label.getAttribute('data-eu-size');
            const sizeItem = label.closest('.ds-hf__size-item');

            if (unit === 'uk' && sizeMap[euSize]) {
              const ukSize = sizeMap[euSize];

              // Check if this UK size has already been shown
              if (shownUkSizes.has(ukSize)) {
                // Hide duplicate UK size
                if (sizeItem) {
                  sizeItem.style.display = 'none';
                }
              } else {
                // Show this UK size and mark as shown
                shownUkSizes.add(ukSize);
                label.textContent = ukSize;
                if (sizeItem) {
                  sizeItem.style.display = '';
                }
              }
            } else {
              // EU mode: show all sizes
              label.textContent = euSize;
              if (sizeItem) {
                sizeItem.style.display = '';
              }
            }
          });
        };

        // Update tab button states based on current unit
        const updateTabStates = () => {
          const tabs = sizeFilter.querySelectorAll('[data-size-tab]');
          tabs.forEach((tab) => {
            const isActive = tab.getAttribute('data-size-tab') === currentUnit;
            if (isActive) {
              tab.classList.add('is-active');
            } else {
              tab.classList.remove('is-active');
            }
            tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });
        };

        // Restore saved unit preference
        const restoreUnitPreference = () => {
          updateTabStates();
          switchSizes(currentUnit);
        };

        // Function to get currently active values from URL/Form
        const getActiveValuesFromUrl = () => {
          const activeValues = [];

          // IMPORTANT: Use currentUnit (from localStorage or tab selection) to determine which filter to read
          // This ensures localStorage is the source of truth, not the URL

          if (currentUnit === 'uk' && ukSizeFilter) {
            // Read from UK filter and convert to EU for display
            const ukCheckboxes = ukSizeFilter.querySelectorAll('.ds-hf__size-input');
            ukCheckboxes.forEach((checkbox) => {
              if (checkbox.getAttribute('data-initially-active') === 'true') {
                const ukValue = checkbox.value;
                // Convert UK value back to ALL matching EU sizes (for duplicate handling)
                Object.keys(sizeMap).forEach((euKey) => {
                  if (sizeMap[euKey] === ukValue) {
                    activeValues.push(euKey);
                  }
                });
              }
            });
          } else {
            // Read from EU filter
            const checkboxes = sizeFilter.querySelectorAll('.ds-hf__size-input');
            checkboxes.forEach((checkbox) => {
              if (checkbox.getAttribute('data-initially-active') === 'true') {
                activeValues.push(checkbox.value);
              }
            });
          }

          return activeValues;
        };

        // Function to sync checkboxes with current state
        const syncCheckboxesWithState = (activeValues) => {
          const checkboxes = sizeFilter.querySelectorAll('.ds-hf__size-input');

          // In UK mode, we need to check all EU sizes that map to the same UK size
          if (currentUnit === 'uk') {
            // Convert active EU values to UK values
            const activeUkValues = new Set();
            activeValues.forEach((euValue) => {
              const ukValue = sizeMap[euValue];
              if (ukValue) {
                activeUkValues.add(ukValue);
              }
            });

            // Check all EU sizes that map to any active UK value
            checkboxes.forEach((checkbox) => {
              const euValue = checkbox.value;
              const ukValue = sizeMap[euValue];
              checkbox.checked = ukValue && activeUkValues.has(ukValue);
            });
          } else {
            // EU mode: simple direct match
            checkboxes.forEach((checkbox) => {
              checkbox.checked = activeValues.indexOf(checkbox.value) !== -1;
            });
          }
        };

        // Function to get selected values from checkboxes
        const getSelectedValues = () => {
          const selectedValues = [];
          const checkboxes = sizeFilter.querySelectorAll('.ds-hf__size-input:checked:not(:disabled)');

          checkboxes.forEach((checkbox) => {
            selectedValues.push(checkbox.value);
          });

          return selectedValues;
        };

        // Function to apply selected values to form
        const applyToForm = (selectedValues) => {
          if (!facetForm || !filterParamName) return;

          // Determine which filter to use based on current unit
          const useUkFilter = currentUnit === 'uk' && ukFilterParamName;

          // When switching to UK mode, we need to convert EU sizes to UK sizes
          let targetValues = selectedValues;
          let targetParamName = filterParamName;

          if (useUkFilter) {
            // Convert EU sizes to UK sizes using the size map
            targetValues = selectedValues.map((euSize) => sizeMap[euSize]).filter(Boolean);
            targetParamName = ukFilterParamName;

            // Remove existing inputs for BOTH filters (EU and UK)
            const euInputs = facetForm.querySelectorAll('input[name="' + filterParamName + '"]');
            euInputs.forEach((input) => input.remove());

            const ukInputs = facetForm.querySelectorAll('input[name="' + ukFilterParamName + '"]');
            ukInputs.forEach((input) => input.remove());
          } else {
            // EU mode: Remove existing inputs for BOTH filters
            const euInputs = facetForm.querySelectorAll('input[name="' + filterParamName + '"]');
            euInputs.forEach((input) => input.remove());

            if (ukFilterParamName) {
              const ukInputs = facetForm.querySelectorAll('input[name="' + ukFilterParamName + '"]');
              ukInputs.forEach((input) => input.remove());
            }
          }

          // Create new hidden inputs for selected values in the target filter
          const pageFilterDiv = facetForm.querySelector('[data-index*="-page"]');

          targetValues.forEach((value) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = targetParamName;
            input.value = value;

            if (pageFilterDiv) {
              pageFilterDiv.appendChild(input);
            } else {
              facetForm.appendChild(input);
            }
          });

          // Update data-initially-active attributes on the current (EU) filter
          const allCheckboxes = sizeFilter.querySelectorAll('.ds-hf__size-input');
          allCheckboxes.forEach((checkbox) => {
            // In UK mode, EU checkboxes should not be marked as active (since we're using UK filter)
            const isSelected = !useUkFilter && selectedValues.indexOf(checkbox.value) !== -1;
            checkbox.setAttribute('data-initially-active', isSelected ? 'true' : 'false');
          });

          // Update data-initially-active attributes on the UK filter if it exists
          if (ukSizeFilter) {
            const ukCheckboxes = ukSizeFilter.querySelectorAll('.ds-hf__size-input');
            ukCheckboxes.forEach((checkbox) => {
              // In UK mode, mark UK checkboxes as active based on converted values
              const isSelected = useUkFilter && targetValues.indexOf(checkbox.value) !== -1;
              checkbox.setAttribute('data-initially-active', isSelected ? 'true' : 'false');
            });
          }
        };

        // Function to close the popup
        const closePopup = () => {
          if (detailsElement) {
            detailsElement.open = false;
          }
        };

        // Function to trigger form submission
        const submitForm = () => {
          if (facetForm) {
            const changeEvent = new Event('change', { bubbles: true });
            facetForm.dispatchEvent(changeEvent);
          }
        };

        // Prevent checkboxes from triggering form submission
        const checkboxes = sizeFilter.querySelectorAll('.ds-hf__size-input');
        checkboxes.forEach((checkbox) => {
          checkbox.addEventListener('change', (e) => {
            e.stopPropagation();
          }, { signal: this.abortController.signal });
        });

        // Function to update reset button visibility
        const updateResetButtonVisibility = () => {
          const resetBtn = sizeFilter.querySelector('.ds-hf__size-reset');
          if (!resetBtn) return;

          const activeValues = getActiveValuesFromUrl();
          const selectedValues = getSelectedValues();

          // Check if either EU or UK filter has active values
          let hasActiveFilters = activeValues.length > 0;

          // Also check UK filter directly
          if (!hasActiveFilters && ukSizeFilter) {
            const ukCheckboxes = ukSizeFilter.querySelectorAll('.ds-hf__size-input');
            ukCheckboxes.forEach((checkbox) => {
              if (checkbox.getAttribute('data-initially-active') === 'true') {
                hasActiveFilters = true;
              }
            });
          }

          const hasChanges = JSON.stringify(activeValues.sort()) !== JSON.stringify(selectedValues.sort());

          if (hasActiveFilters || hasChanges) {
            resetBtn.classList.remove('is-hidden');
          } else {
            resetBtn.classList.add('is-hidden');
          }
        };

        // When popup opens: sync checkboxes with URL state and restore unit preference
        if (detailsElement) {
          detailsElement.addEventListener('toggle', () => {
            if (detailsElement.open) {
              // Save current unit preference (might have been changed by tab click)
              const savedCurrentUnit = currentUnit;

              // Get active values - this might override currentUnit if there are active filters
              const activeValues = getActiveValuesFromUrl();

              // If there are no active values, restore the saved unit preference
              // (user might have clicked a tab without applying yet)
              if (activeValues.length === 0) {
                currentUnit = savedCurrentUnit;
              }

              // Then restore UI based on the determined unit
              restoreUnitPreference();
              syncCheckboxesWithState(activeValues);
              updateResetButtonVisibility();
            }
          }, { signal: this.abortController.signal });
        }

        // Update reset button visibility on checkbox change
        checkboxes.forEach((checkbox) => {
          checkbox.addEventListener('change', () => {
            updateResetButtonVisibility();
          }, { signal: this.abortController.signal });
        });

        // Tab switching (EU/UK)
        const tabs = sizeFilter.querySelectorAll('[data-size-tab]');
        tabs.forEach((tab) => {
          tab.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            const targetUnit = tab.getAttribute('data-size-tab');

            if (targetUnit === currentUnit) {
              return;
            }

            currentUnit = targetUnit;
            this.saveUnitPreference(STORAGE_KEY, targetUnit);
            updateTabStates();
            switchSizes(targetUnit);
          }, { signal: this.abortController.signal });
        });

        // Apply button
        const applyBtn = sizeFilter.querySelector('.ds-hf__size-apply');
        if (applyBtn) {
          applyBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            const selectedValues = getSelectedValues();
            applyToForm(selectedValues);
            closePopup();
            submitForm();
          }, { signal: this.abortController.signal });
        }

        // Reset button
        const resetBtn = sizeFilter.querySelector('.ds-hf__size-reset');
        if (resetBtn) {
          resetBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            checkboxes.forEach((checkbox) => {
              checkbox.checked = false;
            });

            // Also clear the UK filter if it exists
            if (ukSizeFilter) {
              const ukCheckboxes = ukSizeFilter.querySelectorAll('.ds-hf__size-input');
              ukCheckboxes.forEach((checkbox) => {
                checkbox.setAttribute('data-initially-active', 'false');
              });
            }

            applyToForm([]);
            closePopup();
            submitForm();
          }, { signal: this.abortController.signal });
        }

        // Initialize currentUnit from URL on page load
        // Check if there are active filters in the URL and set currentUnit accordingly
        const initializeCurrentUnitFromUrl = () => {
          // Check if UK filter has active values
          let hasUkFilters = false;
          let hasEuFilters = false;

          if (ukSizeFilter) {
            const ukCheckboxes = ukSizeFilter.querySelectorAll('.ds-hf__size-input');
            ukCheckboxes.forEach((checkbox) => {
              if (checkbox.getAttribute('data-initially-active') === 'true') {
                hasUkFilters = true;
              }
            });
          }

          // Check if EU filter has active values
          const euCheckboxes = sizeFilter.querySelectorAll('.ds-hf__size-input');
          euCheckboxes.forEach((checkbox) => {
            if (checkbox.getAttribute('data-initially-active') === 'true') {
              hasEuFilters = true;
            }
          });

          // Override currentUnit based on URL filters (if present)
          if (hasUkFilters && !hasEuFilters) {
            // UK filter is active in URL → switch to UK
            currentUnit = 'uk';
            this.saveUnitPreference(STORAGE_KEY, 'uk');
          } else if (hasEuFilters && !hasUkFilters) {
            // EU filter is active in URL → switch to EU
            currentUnit = 'eu';
            this.saveUnitPreference(STORAGE_KEY, 'eu');
          }
          // If both or neither have filters, keep localStorage default
        };

        initializeCurrentUnitFromUrl();
      }

      // ==================== Generic Filter ====================

      initGenericFilter(detailsElement) {
        const facetForm = this.elements.form;
        const contentElement = detailsElement.querySelector('.ds-hf__content');
        if (!contentElement) return;

        // Check if already initialized
        if (contentElement.hasAttribute('data-filter-initialized')) {
          return;
        }

        const applyBtn = contentElement.querySelector('.ds-hf__filter-apply');
        const resetBtn = contentElement.querySelector('.ds-hf__filter-reset');

        // Skip if this is a size filter (already handled)
        if (contentElement.querySelector('[data-size-filter]')) return;

        // Skip if this is a sort filter (already handled)
        if (detailsElement.hasAttribute('data-sort-filter')) return;

        // Skip if no action buttons found
        if (!applyBtn || !resetBtn) return;

        // Mark as initialized
        contentElement.setAttribute('data-filter-initialized', 'true');

        const filterInputs = contentElement.querySelectorAll('.ds-hf__filter-input');
        if (filterInputs.length === 0) return;

        // Get active filter values from URL state
        const getActiveValuesFromUrl = () => {
          const activeValues = [];
          filterInputs.forEach((input) => {
            if (input.getAttribute('data-initially-active') === 'true') {
              activeValues.push(input.value || input.getAttribute('data-initial-value'));
            }
          });
          return activeValues;
        };

        // Sync inputs with current state
        const syncInputsWithState = (activeValues) => {
          filterInputs.forEach((input) => {
            if (input.type === 'checkbox') {
              input.checked = activeValues.indexOf(input.value) !== -1;

              // Update visual state for color swatches
              const label = input.closest('.ds-hf__color-item')?.querySelector('.ds-hf__color-label')
                         || input.parentElement?.querySelector('label[for="' + input.id + '"]');

              if (label) {
                if (input.checked) {
                  label.classList.add('ds-hf__color-label--active');
                } else {
                  label.classList.remove('ds-hf__color-label--active');
                }
              }
            } else if (input.type === 'number') {
              const initialValue = input.getAttribute('data-initial-value');
              if (initialValue) {
                input.value = initialValue;
              } else {
                input.value = '';
              }
            }
          });
        };

        // Get selected values from inputs
        const getSelectedValues = () => {
          const selectedValues = {};
          filterInputs.forEach((input) => {
            if (input.type === 'checkbox') {
              if (input.checked && !input.disabled) {
                const paramName = input.getAttribute('data-name') || input.name;
                if (!selectedValues[paramName]) {
                  selectedValues[paramName] = [];
                }
                selectedValues[paramName].push(input.value);
              }
            } else if (input.type === 'number') {
              if (input.value) {
                const paramName = input.getAttribute('data-name') || input.name;
                selectedValues[paramName] = input.value;
              }
            }
          });
          return selectedValues;
        };

        // Apply selected values to form
        const applyToForm = (selectedValues) => {
          if (!facetForm) return;

          // Get all unique param names from inputs
          const paramNames = {};
          filterInputs.forEach((input) => {
            const paramName = input.getAttribute('data-name') || input.name;
            paramNames[paramName] = true;
          });

          // Remove existing hidden inputs for these params
          Object.keys(paramNames).forEach((paramName) => {
            const existingInputs = facetForm.querySelectorAll('input[name="' + paramName + '"]');
            existingInputs.forEach((input) => {
              if (!filterInputs.length || Array.from(filterInputs).indexOf(input) === -1) {
                input.remove();
              }
            });
          });

          // Create new hidden inputs for selected values
          const pageFilterDiv = facetForm.querySelector('[data-index*="-page"]');

          Object.keys(selectedValues).forEach((paramName) => {
            const values = selectedValues[paramName];

            if (Array.isArray(values)) {
              values.forEach((value) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = paramName;
                input.value = value;

                if (pageFilterDiv) {
                  pageFilterDiv.appendChild(input);
                } else {
                  facetForm.appendChild(input);
                }
              });
            } else {
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = paramName;
              input.value = values;

              if (pageFilterDiv) {
                pageFilterDiv.appendChild(input);
              } else {
                facetForm.appendChild(input);
              }
            }
          });

          // Update data-initially-active attributes
          filterInputs.forEach((input) => {
            if (input.type === 'checkbox') {
              const paramName = input.getAttribute('data-name') || input.name;
              const isSelected = selectedValues[paramName] && selectedValues[paramName].indexOf(input.value) !== -1;
              input.setAttribute('data-initially-active', isSelected ? 'true' : 'false');
            } else if (input.type === 'number') {
              const paramName = input.getAttribute('data-name') || input.name;
              const hasValue = selectedValues[paramName] && selectedValues[paramName] !== '';
              input.setAttribute('data-initially-active', hasValue ? 'true' : 'false');
              if (hasValue) {
                input.setAttribute('data-initial-value', selectedValues[paramName]);
              } else {
                input.removeAttribute('data-initial-value');
              }
            }
          });
        };

        // Close the filter popup
        const closePopup = () => {
          if (detailsElement) {
            detailsElement.open = false;
          }
        };

        // Submit the form
        const submitForm = () => {
          if (facetForm) {
            const changeEvent = new Event('change', { bubbles: true });
            facetForm.dispatchEvent(changeEvent);
          }
        };

        // Check if there are any active filters or changes
        const hasActiveFiltersOrChanges = () => {
          let hasActive = false;
          let hasChanges = false;

          filterInputs.forEach((input) => {
            const initiallyActive = input.getAttribute('data-initially-active') === 'true';
            const initialValue = input.getAttribute('data-initial-value');

            if (input.type === 'checkbox') {
              if (initiallyActive) {
                hasActive = true;
              }
              if (input.checked !== initiallyActive) {
                hasChanges = true;
              }
            } else if (input.type === 'number') {
              if (initiallyActive && initialValue) {
                hasActive = true;
              }
              const currentValue = input.value || '';
              const compareInitialValue = initialValue || '';
              if (currentValue !== compareInitialValue) {
                hasChanges = true;
              }
            }
          });

          return hasActive || hasChanges;
        };

        // Update reset button visibility
        const updateResetButtonVisibility = () => {
          if (!resetBtn) return;

          if (hasActiveFiltersOrChanges()) {
            resetBtn.classList.remove('is-hidden');
          } else {
            resetBtn.classList.add('is-hidden');
          }
        };

        // Prevent inputs from triggering form submission
        filterInputs.forEach((input) => {
          input.addEventListener('change', (e) => {
            e.stopPropagation();
            updateResetButtonVisibility();

            // Update visual state for checkboxes (especially for color swatches)
            if (input.type === 'checkbox') {
              const label = input.closest('.ds-hf__color-item')?.querySelector('.ds-hf__color-label')
                         || input.parentElement?.querySelector('label[for="' + input.id + '"]');

              if (label) {
                if (input.checked) {
                  label.classList.add('ds-hf__color-label--active');
                } else {
                  label.classList.remove('ds-hf__color-label--active');
                }
              }
            }
          }, { signal: this.abortController.signal });

          input.addEventListener('input', (e) => {
            e.stopPropagation();
            updateResetButtonVisibility();
          }, { signal: this.abortController.signal });
        });

        // When popup opens: sync inputs with URL state
        detailsElement.addEventListener('toggle', () => {
          if (detailsElement.open) {
            const activeValues = getActiveValuesFromUrl();
            syncInputsWithState(activeValues);
            updateResetButtonVisibility();
          }
        }, { signal: this.abortController.signal });

        // Apply button
        applyBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const selectedValues = getSelectedValues();
          applyToForm(selectedValues);
          closePopup();
          submitForm();
        }, { signal: this.abortController.signal });

        // Reset button
        resetBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          filterInputs.forEach((input) => {
            if (input.type === 'checkbox') {
              input.checked = false;
            } else if (input.type === 'number') {
              input.value = '';
            }
          });

          applyToForm({});
          closePopup();
          submitForm();
        }, { signal: this.abortController.signal });
      }

      // ==================== Sort Filter ====================

      initSortFilter(sortFilter) {
        // Check if already initialized to avoid duplicate event listeners
        if (sortFilter.hasAttribute('data-filter-initialized')) {
          return;
        }
        sortFilter.setAttribute('data-filter-initialized', 'true');

        const detailsElement = sortFilter.closest('details.ds-hf__chip');
        const facetForm = this.elements.form;
        const sortOptions = sortFilter.querySelectorAll('[data-sort-option]');

        if (sortOptions.length === 0) return;

        // Close the filter popup
        const closePopup = () => {
          if (detailsElement) {
            detailsElement.open = false;
          }
        };

        // Submit the form
        const submitForm = () => {
          if (facetForm) {
            const changeEvent = new Event('change', { bubbles: true });
            facetForm.dispatchEvent(changeEvent);
          }
        };

        // Add change event listener to all radio buttons
        sortOptions.forEach((radio) => {
          radio.addEventListener('change', (e) => {
            e.stopPropagation();

            // Immediately submit the form and close popup when a sort option is selected
            closePopup();
            submitForm();
          }, { signal: this.abortController.signal });
        });
      }

      // ==================== Initialize All Filters ====================

      initializeAllFilters() {
        // Initialize all size filters
        const sizeFilters = this.querySelectorAll('[data-size-filter]');
        sizeFilters.forEach((sizeFilter) => {
          this.initSizeFilter(sizeFilter);
        });

        // Initialize sort filter
        const sortFilter = this.querySelector('[data-sort-filter]');
        if (sortFilter) {
          this.initSortFilter(sortFilter);
        }

        // Initialize all generic filters (non-size, non-sort filters)
        const allFilterChips = this.querySelectorAll('.ds-hf__chip');
        allFilterChips.forEach((chip) => {
          this.initGenericFilter(chip);
        });
      }

      // ==================== Sync Active Filters to Form ====================

      syncAllActiveFiltersToForm() {
        if (!this.elements.form) return;

        // Step 1: Collect all param names that belong to our filters
        const allFilterInputs = this.querySelectorAll(
          '.ds-hf__filter-input, .ds-hf__size-input'
        );
        const filterParamNames = new Set();
        allFilterInputs.forEach((input) => {
          const paramName = input.getAttribute('data-name') || input.name;
          if (paramName) {
            filterParamNames.add(paramName);
          }
        });

        // Step 2: Remove ALL existing hidden inputs for these param names
        filterParamNames.forEach((paramName) => {
          const existingHiddenInputs = this.elements.form.querySelectorAll(
            `input[type="hidden"][name="${paramName}"]`
          );
          existingHiddenInputs.forEach((input) => {
            input.remove();
          });
        });

        // Step 3: Add hidden inputs ONLY for currently active filters
        const allActiveInputs = this.querySelectorAll(
          '.ds-hf__filter-input[data-initially-active="true"], .ds-hf__size-input[data-initially-active="true"]'
        );

        allActiveInputs.forEach((input) => {
          const paramName = input.getAttribute('data-name') || input.name;
          const value = input.value;

          if (!paramName || !value) return;

          // Create hidden input
          const hiddenInput = document.createElement('input');
          hiddenInput.type = 'hidden';
          hiddenInput.name = paramName;
          hiddenInput.value = value;

          // Add to form
          const pageFilterDiv = this.elements.form.querySelector('[data-index*="-page"]');
          if (pageFilterDiv) {
            pageFilterDiv.appendChild(hiddenInput);
          } else {
            this.elements.form.appendChild(hiddenInput);
          }
        });
      }

      // ==================== PubSub Integration ====================

      subscribeToPubSub() {
        if (typeof FoxTheme === 'undefined' || !FoxTheme.pubsub) return;

        this.pubsubUnsubscribe = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
          () => {
            // Small delay to ensure DOM is fully updated
            setTimeout(() => {
              this.cacheElements();
              this.initMoreFiltersButton();
              this.restoreFiltersExpandedState();
              this.initializeAllFilters();
              this.syncAllActiveFiltersToForm();
            }, 100);
          }
        );
      }

      // ==================== LocalStorage Helpers ====================

      getSavedUnitPreference(storageKey) {
        try {
          return localStorage.getItem(storageKey);
        } catch (error) {
          console.error('Error reading from localStorage:', error);
          return null;
        }
      }

      saveUnitPreference(storageKey, unit) {
        try {
          localStorage.setItem(storageKey, unit);
        } catch (error) {
          console.error('Error writing to localStorage:', error);
        }
      }
    }
  );
}
